import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

const generateLoanValueIssuedAuditExcel = async (data) => {
  try {
    if (!data || !Array.isArray(data)) {
      throw new Error("Invalid data format");
    }

    const branchGroupedData = data.reduce((acc, row) => {
      if (!acc[row.branchName]) acc[row.branchName] = {};
      if (!acc[row.branchName][row.loanTypeName]) acc[row.branchName][row.loanTypeName] = {};
      acc[row.branchName][row.loanTypeName][row.month] = {
        totalAmount: row.totalAmount,
        interestRate: row.interestRate,
      };
      return acc;
    }, {});

    const branches = Object.keys(branchGroupedData);
    const allMonths = [...new Set(data.map((r) => r.month))].sort();

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Audit System";
    workbook.created = new Date();
    const sheet = workbook.addWorksheet("Loan Value Report", {
      pageSetup: { paperSize: 9, orientation: "landscape" },
    });

    const columnsPerBranch = allMonths.length * 2 + 1;
    const totalColumns = branches.length * (columnsPerBranch + 1) - 1;
    sheet.mergeCells(1, 1, 1, totalColumns);
    const titleCell = sheet.getCell(1, 1);
    titleCell.value = "Loan Value Issued Report";
    titleCell.font = { size: 16, bold: true };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(1).height = 30;

    let startCol = 1;
    branches.forEach((branchName, branchIndex) => {
      const endCol = startCol + columnsPerBranch - 1;
      sheet.mergeCells(3, startCol, 3, endCol);
      const cell = sheet.getCell(3, startCol);
      cell.value = branchName;
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.font = { bold: true, size: 12 };
      startCol = endCol + 2;
    });

    const headerRow = [];
    branches.forEach((branchName, branchIndex) => {
      headerRow.push("Loan Type");
      allMonths.forEach((month) => {
        headerRow.push(`${month} Amount`, `${month} Rate`);
      });
      if (branchIndex < branches.length - 1) {
        headerRow.push("");
      }
    });
    sheet.getRow(4).values = headerRow;
    sheet.getRow(4).eachCell((cell) => {
      cell.font = { bold: true, size: 11 };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      if (cell.value !== "") {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }
    });
    sheet.getRow(4).height = 25;

    const allLoanTypes = [...new Set(data.map(r => r.loanTypeName))];
    
    allLoanTypes.forEach((loanType) => {
      const rowValues = [];
      branches.forEach((branchName, branchIndex) => {
        const loanData = branchGroupedData[branchName]?.[loanType] || {};
        rowValues.push(loanType);
        allMonths.forEach((month) => {
          const mData = loanData[month] || { totalAmount: 0, interestRate: 0 };
          rowValues.push(mData.totalAmount, mData.interestRate);
        });
        if (branchIndex < branches.length - 1) {
          rowValues.push("");
        }
      });
      const row = sheet.addRow(rowValues);
      row.eachCell((cell, colNumber) => {
        if (cell.value !== "") {
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          const adjustedColInBranch = ((colNumber - 1) % (columnsPerBranch + 1)) + 1;
          if (adjustedColInBranch > 1 && adjustedColInBranch % 2 === 0) {
            cell.numFmt = '#,##0.00';
          }
        }
      });
    });

    sheet.columns.forEach((col, index) => {
      const colNum = index + 1;
      let isGapColumn = false;
      let currentCol = 1;
      for (let i = 0; i < branches.length - 1; i++) {
        currentCol += columnsPerBranch;
        if (colNum === currentCol) {
          isGapColumn = true;
          break;
        }
        currentCol += 1;
      }
      col.width = isGapColumn ? 5 : (colNum % (columnsPerBranch + 1) === 1 ? 25 : 15);
    });

    sheet.views = [{ state: "frozen", ySplit: 4 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = `Loan_Value_Issued_Report_${dayjs().format("YYYY-MM-DD_HHmmss")}.xlsx`;
    saveAs(blob, fileName);

    return true;
  } catch (error) {
    console.error("Error generating Excel file:", error);
    throw error;
  }
};

export default generateLoanValueIssuedAuditExcel;